import { useEffect, useRef } from 'react';
import { notification } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from 'util/api';

const KEY = 'forecasting';
const RUN_KEY = [KEY, 'run'];

const runInProgress = (status) => status === 'pending' || status === 'running';

export const usePlanQuery = () => useQuery({
  queryKey: [KEY, 'plan'],
  queryFn: () => api.getForecastingPlan(),
  staleTime: 5 * 60 * 1000,
});

export const useAccuracyQuery = () => useQuery({
  queryKey: [KEY, 'accuracy'],
  queryFn: () => api.getForecastingAccuracy(),
  staleTime: 30 * 60 * 1000,
});

export const useSettingsQuery = () => useQuery({
  queryKey: [KEY, 'settings'],
  queryFn: () => api.getForecastingSettings(),
});

export const useBrewsQuery = () => useQuery({
  queryKey: [KEY, 'brews'],
  queryFn: () => api.getForecastingBrews(),
});

/** Every edit changes the plan, so mutations refresh all Forecasting queries. */
const useForecastingMutation = (mutationFn) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries([KEY]),
  });
};

/**
 * Manual BigQuery forecast. Polls while a run is open, including one started elsewhere.
 * A finished run reloads the plan. Idle after a run this tab did not see still reloads,
 * because the other tab's status check clears the server record.
 */
export const useForecastRun = () => {
  const queryClient = useQueryClient();
  const prevStatus = useRef(null);
  const handledAt = useRef(0);
  const statusQuery = useQuery({
    queryKey: RUN_KEY,
    queryFn: () => api.getForecastingRunStatus(),
    refetchInterval: (data) => (runInProgress(data?.status) ? 5000 : false),
  });
  const { data: runState, dataUpdatedAt, fetchStatus } = statusQuery;

  useEffect(() => {
    if (!runState) return undefined;
    const previous = prevStatus.current;
    prevStatus.current = runState.status;

    const reload = () => api.getForecastingPlan({ refresh: true }).then((plan) => {
      queryClient.setQueryData([KEY, 'plan'], plan);
      queryClient.invalidateQueries([KEY, 'accuracy']);
      notification.success({ message: 'Forecast updated' });
    });

    if (runState.status === 'failed' || runState.status === 'done') {
      if (handledAt.current === dataUpdatedAt) return undefined;
      handledAt.current = dataUpdatedAt;
    }
    if (runState.status === 'failed') {
      notification.error({
        message: 'Forecast run failed',
        description: runState.error,
      });
      queryClient.setQueryData(RUN_KEY, { status: 'idle' });
      return undefined;
    }
    if (runState.status === 'done') {
      reload().finally(() => queryClient.setQueryData(RUN_KEY, { status: 'idle' }));
      return undefined;
    }
    if (runInProgress(previous) && runState.status === 'idle') reload();
    return undefined;
  }, [dataUpdatedAt, queryClient, runState]);

  const start = useMutation({
    mutationFn: () => api.startForecastingRun(),
    onSuccess: (data) => queryClient.setQueryData(RUN_KEY, data),
    onError: () => queryClient.invalidateQueries(RUN_KEY),
  });

  return {
    running: runInProgress(runState?.status) || start.isLoading,
    statusUnknown: fetchStatus === 'fetching' && runState === undefined,
    start: start.mutate,
  };
};

/** Re-read BigQuery (bypassing the backend cache) and rebuild the plan. */
export const useRefreshPlan = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.getForecastingPlan({ refresh: true }),
    onSuccess: (data) => queryClient.setQueryData([KEY, 'plan'], data),
  });
};

export const useSaveSettings = () => useForecastingMutation(api.updateForecastingSettings);
export const useSetBlend = () => useForecastingMutation(api.setForecastingBlend);
export const useSaveBeerRule = () => useForecastingMutation(api.saveForecastingBeerRule);
export const useDeleteBeerRule = () => useForecastingMutation(api.deleteForecastingBeerRule);
export const useSaveMixComponents = () => useForecastingMutation(
  ({ mixBeerName, components }) => api.saveForecastingMixComponents(mixBeerName, components),
);
export const useCreateBrew = () => useForecastingMutation(api.createForecastingBrew);
export const useCreateBrewsBulk = () => useForecastingMutation(api.createForecastingBrewsBulk);
export const useUpdateBrew = () => useForecastingMutation(
  ({ id, ...brew }) => api.updateForecastingBrew(id, brew),
);
export const useDeleteBrew = () => useForecastingMutation(api.deleteForecastingBrew);
export const useCreateEvent = () => useForecastingMutation(api.createForecastingEvent);
export const useUpdateEvent = () => useForecastingMutation(
  ({ id, ...event }) => api.updateForecastingEvent(id, event),
);
export const useDeleteEvent = () => useForecastingMutation(api.deleteForecastingEvent);
